import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Dropdown, Spinner, Alert, Tooltip, OverlayTrigger 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import './Factures.css';

export default function Devis() {
    const navigate = useNavigate();
    const [devis, setDevis] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [filtreStatut, setFiltreStatut] = useState('tous');
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'date_proforma', direction: 'desc' });

    useEffect(() => {
        fetchDevis();
        fetchStats();
    }, [filtreStatut]);

    const fetchDevis = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/devis.php`;
            
            if (filtreStatut !== 'tous') {
                url += `?statut=${filtreStatut}`;
            }
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setDevis(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/devis.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { color: '#64748b', icon: 'ri-draft-line', label: 'Brouillon' },
            envoye: { color: '#3b82f6', icon: 'ri-send-plane-line', label: 'Envoyé' },
            accepte: { color: '#10b981', icon: 'ri-check-double-line', label: 'Accepté' },
            refuse: { color: '#ef4444', icon: 'ri-close-circle-line', label: 'Refusé' },
            converti_facture: { color: '#21ACCD', icon: 'ri-file-list-3-line', label: 'Converti' },
            expire: { color: '#f59e0b', icon: 'ri-time-line', label: 'Expiré' }
        };
        const config = configs[statut] || configs.brouillon;
        return (
            <span className="status-badge" style={{ 
                background: `${config.color}15`, 
                color: config.color 
            }}>
                <i className={`${config.icon} me-1 fs-10`}></i>
                {config.label}
            </span>
        );
    };

    const estExpire = (dateValidite) => {
        return new Date(dateValidite) < new Date();
    };

    const filteredDevis = useMemo(() => {
        return devis
            .filter(d => 
                d.numero_proforma?.toLowerCase().includes(filterText.toLowerCase()) ||
                d.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                d.code_client?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_proforma') {
                    return sortConfig.direction === 'asc' 
                        ? new Date(a.date_proforma) - new Date(b.date_proforma)
                        : new Date(b.date_proforma) - new Date(a.date_proforma);
                }
                if (sortConfig.key === 'montant') {
                    return sortConfig.direction === 'asc'
                        ? a.net_a_payer - b.net_a_payer
                        : b.net_a_payer - a.net_a_payer;
                }
                return 0;
            });
    }, [devis, filterText, sortConfig]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false }) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}
        >
            <Button
                variant={variant}
                size="sm"
                onClick={onClick}
                disabled={disabled}
                className="action-btn"
            >
                <i className={icon}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading && !devis.length) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des devis...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </React.Fragment>
        );
    }

    if (error && !devis.length) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchDevis}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-file-copy-line fs-20 text-primary"></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Gestion des devis</h4>
                                    <p className="text-muted small mb-0">Créez et suivez vos devis et propositions commerciales</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                style={{ background: '#21ACCD', border: '0px' }}
                                onClick={() => navigate('/ventes/devis/nouveau')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-add-line"></i>
                                Nouveau devis
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards modernes */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-primary">
                                    <div className="kpi-icon"><i className="ri-file-copy-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Devis (mois)</span>
                                        <h3 className="kpi-value">{stats.mois?.nb_devis || 0}</h3>
                                        <span className="kpi-unit">{formatCurrency(stats.mois?.total_montant || 0)}</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-success">
                                    <div className="kpi-icon"><i className="ri-check-double-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Acceptés</span>
                                        <h3 className="kpi-value">{stats.statuts?.find(s => s.statut === 'accepte')?.nb || 0}</h3>
                                        <span className="kpi-unit">{formatCurrency(stats.statuts?.find(s => s.statut === 'accepte')?.total || 0)}</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-warning">
                                    <div className="kpi-icon"><i className="ri-time-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Expirés</span>
                                        <h3 className="kpi-value">{stats.expires?.nb || 0}</h3>
                                        <span className="kpi-unit">À relancer</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-info">
                                    <div className="kpi-icon"><i className="ri-bar-chart-2-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Conversion</span>
                                        <h3 className="kpi-value">
                                            {stats.conversion?.total > 0 
                                                ? Math.round((stats.conversion?.convertis || 0) / stats.conversion?.total * 100) 
                                                : 0}%
                                        </h3>
                                        <span className="kpi-unit">{stats.conversion?.convertis || 0} convertis</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Filtres */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="5">
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher devis, client..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md="3">
                                    <Form.Select 
                                        value={filtreStatut}
                                        onChange={(e) => setFiltreStatut(e.target.value)}
                                        className="rounded-pill px-3"
                                    >
                                        <option value="tous">Tous les statuts</option>
                                        <option value="brouillon">Brouillons</option>
                                        <option value="envoye">Envoyés</option>
                                        <option value="accepte">Acceptés</option>
                                        <option value="refuse">Refusés</option>
                                        <option value="converti_facture">Convertis</option>
                                    </Form.Select>
                                </Col>
                                <Col md="4">
                                    <div className="text-end">
                                        <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                            <i className="ri-stack-line me-1"></i>
                                            {filteredDevis.length} devis(s)
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des devis */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">N° Devis</th>
                                            <SortableHeader field="date_proforma">Date</SortableHeader>
                                            <th>Client</th>
                                            <th>Validité</th>
                                            <SortableHeader field="montant" className="text-end">Montant TTC</SortableHeader>
                                            <th>Statut</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredDevis.length > 0 ? (
                                            filteredDevis.map((devis) => {
                                                const expire = estExpire(devis.date_validite);
                                                const statutFinal = expire && devis.statut === 'envoye' ? 'expire' : devis.statut;
                                                
                                                return (
                                                    <tr key={devis.id_proforma}>
                                                        <td className="ps-4 fw-semibold">
                                                            <Link 
                                                                to={`/ventes/devis/${devis.id_proforma}`} 
                                                                className="table-link"
                                                                title={devis.numero_proforma}
                                                            >
                                                                {devis.numero_proforma?.length > 12 
                                                                    ? devis.numero_proforma.substring(0, 12) + '...' 
                                                                    : devis.numero_proforma}
                                                            </Link>
                                                         </td>
                                                        <td>{formatDate(devis.date_proforma)}</td>
                                                        <td>
                                                            <div className="fw-semibold">{devis.client_nom}</div>
                                                            <small className="text-muted">{devis.code_client}</small>
                                                        </td>
                                                        <td>
                                                            <span className={expire ? 'text-danger fw-bold' : ''}>
                                                                {formatDate(devis.date_validite)}
                                                            </span>
                                                            {expire && devis.statut === 'envoye' && (
                                                                <Badge bg="danger" className="ms-2 retard-badge" style={{ fontSize: '0.65rem' }}>
                                                                    Expiré
                                                                </Badge>
                                                            )}
                                                        </td>
                                                        <td className="text-end fw-bold text-primary">
                                                            {formatCurrency(devis.net_a_payer)}
                                                        </td>
                                                        <td>{getStatutBadge(statutFinal)}</td>
                                                        <td className="text-center pe-4">
                                                            <div className="d-flex gap-2 justify-content-center">
                                                                <ActionButton
                                                                    variant="outline-info"
                                                                    icon="ri-eye-line"
                                                                    onClick={() => navigate(`/ventes/DetailDevis/${devis.id_proforma}`)}
                                                                    tooltip="Voir détails"
                                                                />
                                                                {(devis.statut === 'brouillon' || devis.statut === 'envoye') && (
                                                                    <ActionButton
                                                                        variant="outline-primary"
                                                                        icon={devis.statut === 'brouillon' ? 'ri-pencil-line' : 'ri-exchange-line'}
                                                                        onClick={() => navigate(`/ventes/devis/modifier/${devis.id_proforma}`)}
                                                                        tooltip={devis.statut === 'brouillon' ? 'Modifier' : 'Changer le statut'}
                                                                    />
                                                                )}
                                                                {devis.statut === 'accepte' && (
                                                                    <ActionButton
                                                                        variant="outline-success"
                                                                        icon="ri-file-list-3-line"
                                                                        onClick={() => navigate(`/ventes/factures/nouveau?devis=${devis.id_proforma}`)}
                                                                        tooltip="Convertir en facture"
                                                                    />
                                                                )}
                                                                {devis.statut === 'brouillon' && (
                                                                    <ActionButton
                                                                        variant="outline-warning"
                                                                        icon="ri-mail-send-line"
                                                                        onClick={() => navigate(`/ventes/devis/envoyer/${devis.id_proforma}`)}
                                                                        tooltip="Envoyer par email"
                                                                    />
                                                                )}
                                                            </div>
                                                        </td>
                                                     </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                                    <h5 className="mt-3">Aucun devis trouvé</h5>
                                                    <p className="text-muted mb-3">
                                                        {filterText 
                                                            ? "Aucun résultat ne correspond à votre recherche"
                                                            : "Commencez par créer votre premier devis"}
                                                    </p>
                                                    {filterText ? (
                                                        <Button 
                                                            variant="outline-primary"
                                                            onClick={() => setFilterText('')}
                                                            className="rounded-pill"
                                                        >
                                                            <i className="ri-refresh-line me-2"></i>
                                                            Réinitialiser
                                                        </Button>
                                                    ) : (
                                                        <Button 
                                                            style={{ background: '#21ACCD', border: 'none' }}
                                                            onClick={() => navigate('/ventes/devis/nouveau')}
                                                            className="rounded-pill"
                                                        >
                                                            <i className="ri-add-line me-2"></i>
                                                            Créer un devis
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #4361ee; }
                .kpi-success { border-left-color: #10b981; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-info { border-left-color: #21ACCD; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e8f0fe; color: #4361ee; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.25rem 0.75rem;
                    border-radius: 20px;
                    font-size: 0.75rem;
                    font-weight: 500;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                .btn-outline-primary:hover {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    border-color: #4361ee;
                    color: white;
                }
                .btn-outline-success:hover {
                    background: linear-gradient(135deg, #10b981, #059669);
                    border-color: #10b981;
                    color: white;
                }
                .btn-outline-warning:hover {
                    background: linear-gradient(135deg, #f59e0b, #d97706);
                    border-color: #f59e0b;
                    color: white;
                }
                .btn-outline-secondary:hover {
                    background: linear-gradient(135deg, #64748b, #475569);
                    border-color: #64748b;
                    color: white;
                }
                
                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Retard Badge */
                .retard-badge {
                    animation: pulse 2s infinite;
                }
                @keyframes pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                    100% { transform: scale(1); }
                }
                
                /* Utilitaires */
                .rounded-3 {
                    border-radius: 12px !important;
                }
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-14 {
                    font-size: 0.875rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </React.Fragment>
    );
}